<template>
  <v-dialog
    v-model="showModal"
    persistent
    max-width="800px"
    @keydown.esc.prevent="closeModal()"
  >
    <el-overlay-loading :loading="modalloading" />
    <v-card class="modalCard">
      <v-card-title class="modalTitle pt-2 pb-1 mb-2" v-if="!modalloading">
        <h3>{{ $t('Asmens dokumentas') }}</h3>
        <v-spacer></v-spacer>
        <v-btn v-if="!readonly" icon @click.native.stop="closeModal()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-2 pb-2">
        <v-row v-if="item">
          <v-col cols="12" md="12">
            <div class="mb-1">
              <strong>{{ $t('Asmens dokumento rūšis') }}:</strong> {{ item.person_document_kind_title_lt || '-' }}
            </div>
            <div class="mb-1">
              <strong>{{ $t('Asmens dokumento numeris') }}:</strong> {{ item.person_document_number || '-' }}
            </div>
            <div class="mb-1">
              <strong>{{ $t('Asmens dokumento išdavimo data') }}:</strong> {{ item.person_document_date ? $moment(item.person_document_date).format('YYYY-MM-DD') : '-' }}
            </div>
          </v-col>
          <v-col cols="12">
            <div class="mb-1">
              <strong>{{ $t('Asmens dokumentai') }}:</strong>
              <div class="mt-2">
                <el-file-upload-table
                  :value="documentFiles"
                  :readonly="readonly"
                  :disabled="readonly"
                  :loading="downloading || uploading"
                  :can-delete-file="canDeleteFile"
                  @file-downloaded="handleFileDownload"
                  @files-added="handleFilesAdded"
                  @file-removed="handleFileRemoved"
                />
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="pt-0 pb-2">
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click.native.stop="closeModal()"
        >
          {{ $t('Uždaryti') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Delete Confirmation Modal -->
    <v-dialog
      v-model="showDeleteConfirmation"
      max-width="400px"
      persistent
    >
      <v-card>
        <v-card-title class="headline">
          {{ $t('Patvirtinti šalinimą') }}
        </v-card-title>
        <v-card-text>
          {{ $t('Ar tikrai norite pašalinti šį failą?') }}
          <div v-if="fileToDelete" class="mt-2">
            <strong>{{ fileToDelete.name }}</strong>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            text
            @click="cancelDelete"
          >
            {{ $t('Atšaukti') }}
          </v-btn>
          <v-btn
            color="error"
            text
            @click="confirmDelete"
            :loading="deleting"
          >
            {{ $t('Pašalinti') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import math from '@/mixins/math'
import ElFileUploadTable from '@/components/elements/ElFileUploadTable.vue'

export default {
  name: 'ModalPersonDocument',
  components: {
    ElFileUploadTable
  },
  mixins: [math],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: true,
    },
    uploadMode: {
      type: String,
      default: 'instant', // 'instant' for JournalReceivedData, 'array' for ProcuratorForm/RepresentativeForm/PrincipalForm
      validator: (value) => ['instant', 'array'].includes(value)
    },
    inJournalLatestStatusDate: {
      type: String,
      default: null, // Required for JournalReceivedDataForm to check deletion eligibility
    },
    inJournalId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      modalloading: false,
      downloading: false,
      uploading: false,
      showDeleteConfirmation: false,
      fileToDelete: null,
      deleting: false,
    }
  },
  computed: {
    showModal: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    $moment() {
      return moment
    },
    documentFiles() {
      if (!this.item) return []

      const files = []

      // Handle person_document_files
      if (this.item.person_document_files) {
        if (Array.isArray(this.item.person_document_files)) {
          this.item.person_document_files.forEach(file => {
            files.push({
              name: file.name || file.original_filename || file.filename || 'document',
              size: file.size || 0,
              type: file.mime_type || 'application/octet-stream',
              id: file.id || null,
              file: file,
              uploadDate: file.uploadDate || file.created_at || file.upload_date
            })
          })
        } else {
          // Single file object
          files.push({
            name: this.item.person_document_files.name || this.item.person_document_files.original_filename || this.item.person_document_files.filename || 'document',
            size: this.item.person_document_files.size || 0,
            type: this.item.person_document_files.mime_type || 'application/octet-stream',
            id: this.item.person_document_files.id || null,
            file: this.item.person_document_files,
            uploadDate: this.item.person_document_files.uploadDate || this.item.person_document_files.created_at || this.item.person_document_files.upload_date
          })
        }
      }

      // Handle person_documents array
      if (this.item.person_documents && Array.isArray(this.item.person_documents)) {
        this.item.person_documents.forEach(doc => {
          files.push({
            name: doc.original_filename || doc.filename || 'document',
            size: doc.size || 0,
            type: doc.mime_type || 'application/octet-stream',
            id: doc.id || null,
            file: doc.file || null,
            uploadDate: doc.uploadDate || doc.created_at || doc.upload_date
          })
        })
      }

      return files
    },

    canDeleteFile() {
      return (fileItem) => {
        if (this.uploadMode === 'instant') {
          // For JournalReceivedDataForm: can delete only files uploaded after InJournal latest status date
          if (!this.inJournalLatestStatusDate) {
            return false // No date provided, cannot delete
          }

          const fileUploadDate = fileItem.uploadDate || fileItem.created_at
          if (!fileUploadDate) {
            return false // No upload date, cannot delete
          }

          // Compare file upload date with InJournal latest status date
          const fileDate = new Date(fileUploadDate)
          const statusDate = new Date(this.inJournalLatestStatusDate)

          return fileDate > statusDate
        } else {
          // For ProcuratorForm/RepresentativeForm/PrincipalForm: can delete only if more than one file exists
          return this.documentFiles.length > 1
        }
      }
    }
  },
  methods: {
    closeModal() {
      this.showModal = false
    },
    openFile(file) {
      var fileLink = document.createElement('a')
      fileLink.href = file.file
      fileLink.setAttribute(
        'download',
        this.safeFileName(file.original_filename)
      )
      document.body.appendChild(fileLink)
      fileLink.click()
    },
    handleBinaryFile(fileData) {
      try {
        // Handle binary file data - could be base64 encoded or raw binary
        let blob
        let filename = 'document'

        if (fileData.original_filename) {
          filename = fileData.original_filename
        }

        if (fileData.file) {
          // If it's already a file URL or blob URL
          this.openFile(fileData)
          return
        }

        if (fileData.data) {
          // If it's base64 encoded data
          const byteCharacters = atob(fileData.data)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          blob = new Blob([byteArray], { type: fileData.mime_type || 'application/octet-stream' })
        } else if (fileData instanceof File) {
          // If it's already a File object
          blob = fileData
          filename = fileData.name
        } else {
          // If it's raw binary data
          blob = new Blob([fileData], { type: 'application/octet-stream' })
        }

        // Create download link
        const url = window.URL.createObjectURL(blob)
        const fileLink = document.createElement('a')
        fileLink.href = url
        fileLink.setAttribute('download', this.safeFileName(filename))
        document.body.appendChild(fileLink)
        fileLink.click()
        document.body.removeChild(fileLink)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.$root.$emit('DATA_ERROR', 'Error processing file: ' + error.message)
      }
    },
    handleFileDownload(fileItem) {
      // The ElFileUploadTable component now handles the download directly
      // This method is called after the download is completed
    },

    async handleFilesAdded(newFiles) {
      if (this.uploadMode === 'instant') {
        // Save files instantly to backend (JournalReceivedData context)
        await this.saveFilesToBackend(newFiles)
      } else {
        // Add files to person's file array (ProcuratorForm/RepresentativeForm/PrincipalForm context)
        this.addFilesToPersonArray(newFiles)
      }
    },

    async saveFilesToBackend(files) {
      this.uploading = true

      const returnFiles = []
      try {
        for (const fileItem of files) {
          const formData = new FormData()
          formData.append('file', fileItem.file)
          formData.append('original_name', fileItem.name)
          formData.append('person_id', this.item.id)

          const response = await this.$axios.post(`/in-journals/${this.inJournalId}/uploads/person-document`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })

          returnFiles.push(response.data.upload)
        }

        // Emit event to parent to refresh data
        this.$emit('files-uploaded', returnFiles)

      } catch (error) {
        console.error('Upload error:', error)
        this.$root.$emit('DATA_ERROR', this.getApiErrorMessage(error, 'Failed to upload files'))
      } finally {
        this.uploading = false
      }
    },

    addFilesToPersonArray(files) {
      // Add files to the person's file array for later saving
      if (!this.item.person_document_files) {
        this.$set(this.item, 'person_document_files', [])
      }

      // Convert file items to the expected format
      const formattedFiles = files.map(fileItem => ({
        name: fileItem.name,
        size: fileItem.size,
        type: fileItem.type,
        file: fileItem.file,
        uploadDate: fileItem.uploadDate,
        id: null // Will be set when saved to backend
      }))

      // Add to the person's file array
      this.item.person_document_files.push(...formattedFiles)

      // Emit event to parent
      this.$emit('files-added-to-person', formattedFiles)
    },

    handleFileRemoved(fileToRemove) {
      // Show confirmation modal before deleting
      this.fileToDelete = fileToRemove
      this.showDeleteConfirmation = true
    },

    getApiErrorMessage(error, defaultMessage = 'Failed to remove file') {
      const data = error?.response?.data
      if (!data) return defaultMessage
      const errors = data.errors
      if (errors !== undefined && errors !== null) {
        return Array.isArray(errors) ? errors[0] : errors
      }
      return data.message || defaultMessage
    },

    async removeFileFromBackend(fileId) {
      await this.$axios.delete(`in-journals/${this.inJournalId}/uploads/person-document/${fileId}`)
      // Remove the deleted file from the local array by fileId
      if (this.item.person_documents !== undefined) {
        const index = this.item.person_documents.findIndex(file => file.id === fileId)
        if (index > -1) {
          this.item.person_documents.splice(index, 1)
        }
      }
      this.$emit('file-removed-from-backend', fileId)
    },

    removeFileFromPersonArray(fileToRemove) {
      let index = -1;
      if (fileToRemove.id) {
        // Add to files to delete array
        if (!this.item.files_to_delete) {
          this.$set(this.item, 'files_to_delete', [])
        }
        this.item.files_to_delete.push(fileToRemove.id)
        // Remove by id
        index = this.item.person_documents.findIndex(file => file.id === fileToRemove.id)

        if (index > -1) {
          this.item.person_documents.splice(index, 1)
          this.$emit('file-removed-from-person', fileToRemove)
        }
      } else {
        // Remove by name and size if no id
        index = this.item.person_document_files.findIndex(file =>
          file.name === fileToRemove.name && file.size === fileToRemove.size
        )
        if (index > -1) {
          this.item.person_document_files.splice(index, 1)
          this.$emit('file-removed-from-person', fileToRemove)
        }
      }
    },

    cancelDelete() {
      this.showDeleteConfirmation = false
      this.fileToDelete = null
    },

    async confirmDelete() {
      if (!this.fileToDelete) return

      this.deleting = true

      try {
        if (this.uploadMode === 'instant') {
          // Remove from backend if it has an ID
          if (this.fileToDelete.id) {
            await this.removeFileFromBackend(this.fileToDelete.id)
          }
        } else {
          // Remove from person's file array
          this.removeFileFromPersonArray(this.fileToDelete)
        }

        this.$root.$emit('DATA_SUCCESS', 'File removed successfully')
      } catch (error) {
        console.error('Error removing file:', error)
        this.$root.$emit('DATA_ERROR', this.getApiErrorMessage(error))
      } finally {
        this.deleting = false
        this.showDeleteConfirmation = false
        this.fileToDelete = null
      }
    },

    downloadDocument() {
      if (!this.item) return

      this.downloading = true

      if (this.item.id) {
        this.$axios
          .get(`uploads/${this.item.id}/download`)
          .then((res) => {
            this.openFile(res.data)
          })
          .catch((err) => {
            const errData = JSON.parse(
              Buffer.from(err.response.data).toString('utf8')
            )
            this.$root.$emit('DATA_ERROR', errData.message)
          })
          .finally(() => {
            this.downloading = false
          })
      } else if (this.item.person_document_file) {
        // If no file_id but file object exists, handle binary file data
        this.handleBinaryFile(this.item.person_document_file)
        this.downloading = false
      } else {
        this.$root.$emit('DATA_ERROR', 'No document file available')
        this.downloading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.modalCard {
  border-radius: 8px;
}

.modalTitle {
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}
</style>
